import { getStrbufSummary } from '../str-buf/getStrbufSummary';
import { MaybeStrsDict, StrsDict } from '../types';
import { isStr } from '../utils/common';
import { Warning, WarningCode, WithWarnings } from '../warnings';
import { HeaderParseOptions } from './parseHeaders.types';

const rxRecipLine = /^(:?to|cc|bcc)\s*:.*,\s*$/i;

/**
 * Splits a string by :
 * The result is not mime word decoded, you need to do your own decoding based
 * on the rules for the specific header key
 *
 * @param {String} line Single header key + value, might include linebreaks as well if folded
 * @return {Object} And object of {key, value}
 */
function parseHeader(line: string): { key: string; value: string } {
	// const line = header.replace(/(?:\r?\n|\r)[ \t]*/g, ' ').trim();
	const match = line.match(/^\s*([^:]+):(.*)$/);
	const key = (match?.[1] ?? '').trim().toLowerCase();
	const value = (match?.[2] ?? '').trimLeft();

	return {
		key: key,
		value: value,
	};
}

/**
 * Parses a block of header lines. Does not decode mime words as every
 * header might have its own rules (eg. formatted email addresses and such)
 *
 * @param {String} emailOrHeaders Headers string
 * @return {Object.badLines} A bool indicating if there were bad header lines that were folded or ignored
 * @return {Object.headers} An object of headers, where header keys are object keys. NB! Several values with the same key make up an Array
 */
export function parseHeaders(
	emailOrHeaders: string | Buffer,
	options?: HeaderParseOptions
): WithWarnings<MaybeStrsDict> {
	const warnings: Warning[] = [];
	options = options || {};

	const emailOrHeadersStr = isStr(emailOrHeaders)
		? emailOrHeaders
		: emailOrHeaders.toString('latin1');

	// Ensure header str is trimmed properly... we will keep trailing whitespace
	// in case last header is e.g. subject with trailing space
	const headersStr = emailOrHeadersStr
		.trimStart()
		// Remove everything after double newline
		// TODO: RDJ: Check these 2 regex for catastrophic backtracking...
		.replace(/\r?\n\r?\n[^]*$/g, '')
		// Remove any trailing newlines
		.replace(/[\r\n]+$/, '');

	const rawHeaders = [];

	// Undo folding white space
	{
		const lines = headersStr.split(/\r?\n|\r/);

		let i = 0;

		if (options.skipStartingPop3) {
			while (
				i < lines.length &&
				lines[i].indexOf(':') === -1 &&
				lines[i].startsWith('+OK ')
			) {
				++i;
			}
		}

		for (; i < lines.length; ++i) {
			const line = lines[i];
			// Folding whitespace
			if (rawHeaders.length && line.match(/^\s/)) {
				rawHeaders[rawHeaders.length - 1] += line;
			} else if (line.indexOf(':') < 0) {
				// If this is the first line, something weird
				if (
					options.skipBoramHeaderLines &&
					(line === '#BORAM-HEADER' || line === '#BORAM-HEADER-END')
				) {
					// Do nothing
				} else if (!rawHeaders.length) {
					// Can't unwrap, so can throw or skip
					if (options.badLinesSkip) {
						warnings.push({
							code: WarningCode.EMAIL_HEADERS_BAD_LINES_SKIPPED,
							info: line,
						});
					} else {
						throw new Error('First header line doesn\'t appear valid (no ":")');
					}
				} else if (
					options.badLinesUnfoldAll ||
					(options.badLinesUnfoldIPhone && 1024 === lines[i - 1].length) ||
					(options.badLinesUnfoldRecip &&
						rxRecipLine.test(rawHeaders[rawHeaders.length - 1]))
				) {
					// We are either unwrapping all bad lines, or one of the special cases
					// iPhone Mail will hard wrap at 1024 chars, without inserting any whitespace, so add to previous
					rawHeaders[rawHeaders.length - 1] += line;
					warnings.push({ code: WarningCode.EMAIL_HEADERS_BAD_LINES_UNFOLDED });
				} else {
					if (options.badLinesSkip) {
						warnings.push({
							code: WarningCode.EMAIL_HEADERS_BAD_LINES_SKIPPED,
							info: line,
						});
					} else {
						throw new Error(
							`Got possible continuing header line (no leading whitespace but no ":", previous line length ${
								lines[i - 1].length
							}, "${lines[i - 1]}")`
						);
					}
				}
			} else {
				// Normal, non-folded header line
				rawHeaders.push(line);
			}
		}
	}

	const headersObj: StrsDict = {};
	for (let i = 0, len = rawHeaders.length; i < len; i++) {
		const headerStr = rawHeaders[i];
		const summary = getStrbufSummary(headerStr);

		if (!summary.allAsciiPrintableCrLf) {
			warnings.push({
				code: WarningCode.EMAIL_HEADERS_NON_ASCII,
				info: headerStr,
			});
		} else {
			const header = parseHeader(headerStr);
			if (!headersObj[header.key]) {
				headersObj[header.key] = [header.value];
			} else {
				headersObj[header.key].push(header.value);
			}
		}
	}

	return {
		warnings: warnings,
		result: headersObj,
	};
}
